import type { Contract } from '../types';

import { ClaimStatus } from '@paid-ui/enums/claim';
import { ContractState, ContractTemplate, ContractType } from '@paid-ui/enums/contract';
import { hasContractChangeFlow, isDigitalOnlyContract } from '@paid-ui/utils/contract';

import { contractManager } from '../model';

const secondaryContractTypes = new Set([
  ContractType.SUBCONTRACT,
  ContractType.SUPPLY_CONTRACT,
  ContractType.SERVICE_CONTRACT,
]);

const ongoingStatusSet = new Set([
  ContractState.SUBMITTED,
  ContractState.RESUBMITTED,
  ContractState.REJECTED,
]);

const contractStatusTextMap: Record<ContractState, string> = {
  [ContractState.EXECUTED]: 'Executed',
  [ContractState.ALL_PAID]: 'All claims paid',
  [ContractState.ABANDONED]: 'Abandoned',
  [ContractState.TERMINATED]: 'Terminated',
  [ContractState.SUBMITTED]: 'Submitted',
  [ContractState.RESUBMITTED]: 'Resubmitted',
  [ContractState.ACCEPTED]: 'Accepted',
  [ContractState.REJECTED]: 'Amendments requested',
};

export const saveStatuses = (contract?: Contract) => {
  if (!contract) return;
  const {
    contractType,
    contractTemplate,
    templateCode,
    isDraftContract,
    contractState,
    progressPayments = [],
    paymentSchedule,
  } = contract;
  const { numberOfParticipants, allContractualPartiesJoined, numberOfApproved, numberOfSigned } =
    contractManager;
  const isPeriodic = Boolean(paymentSchedule);

  const isOneLastPaymentLeft =
    progressPayments.filter(({ claim }) => {
      return (
        !claim || (claim.state && ![ClaimStatus.APPROVED, ClaimStatus.PAID].includes(claim.state))
      );
    }).length === 1;

  const isContractAccepted = contractState === ContractState.ACCEPTED;
  const isContractExecuted = contractState === ContractState.EXECUTED;
  const isContractPostExecuted = [
    ContractState.EXECUTED,
    ContractState.ALL_PAID,
    ContractState.TERMINATED,
  ].includes(contractState);
  const isContractClosed = [
    ContractState.ABANDONED,
    ContractState.ALL_PAID,
    ContractState.TERMINATED,
  ].includes(contractState);

  let contractStateText = 'Loading...';
  if (contract.isDraftContract) {
    contractStateText = 'Not yet shared';
  } else if (contract.contractState === ContractState.SUBMITTED && !allContractualPartiesJoined) {
    contractStateText = 'Parties not yet all joined';
  } else if (ongoingStatusSet.has(contractState) && numberOfApproved < 2) {
    contractStateText = 'Not yet all approved';
  } else if (contractState === ContractState.ACCEPTED && numberOfSigned < numberOfParticipants) {
    contractStateText = 'Not yet all signed';
  } else {
    contractStateText = contractStatusTextMap[contract.contractState] ?? 'Unknown';
  }

  contractManager.contractStateText = contractStateText;
  contractManager.statuses = {
    isPeriodic,
    isDraftContract: Boolean(isDraftContract),
    isContractAccepted,
    isContractClosed,
    isContractExecuted,
    isContractPostExecuted,
    isOffPlatformContract:
      contractTemplate === ContractTemplate.SUPER_CONTRACT
        ? (templateCode?.startsWith('OFF_PLATFORM_') ?? false)
        : (contractTemplate?.startsWith(ContractTemplate.OFF_PLATFORM) ?? false),
    isDigitalOnlyContract: isDigitalOnlyContract(templateCode),
    isOneLastPaymentLeft,
    isPrimeContract: contractType === ContractType.HEAD_CONTRACT,
    // Sits under a head contract. Listed rather than derived as "not the head contract":
    // a funding contract runs alongside the head contract rather than beneath it, and a
    // deed is not a works contract at all, so neither is secondary.
    isSecondaryContract: secondaryContractTypes.has(contractType),
    isFundingContract: contractType === ContractType.FUNDING_CONTRACT,
    isServiceContract: contractType === ContractType.SERVICE_CONTRACT,
    isRBNContract: contractTemplate?.startsWith(ContractTemplate.RBN) ?? false,
    isSuperContract: contractTemplate === ContractTemplate.SUPER_CONTRACT,
    hasChangeFlow: hasContractChangeFlow({ contractTemplate, contractType }),
  };
};
